import { Controller, useFormContext, useWatch } from 'react-hook-form'
import { z } from 'zod'
import {
  Typography,
  Checkbox,
  FormControlLabel,
  TextField,
  Paper,
  Alert,
  Grid,
  Stack,
  List,
  ListItem,
  FormControl,
  FormGroup,
  InputAdornment,
  Link,
} from '@mui/material'
import WarningIcon from '@mui/icons-material/Warning'
import { CampaignInputComponent, CampaignInputComponentProps } from '~/features/campaign_input/CampaignInputComponent'
import {
  CampaignSnsRequirementSnsPostType,
  CampaignSnsRequirementSnsType,
  FormatVersion,
  LanguageType,
} from '~/generated/graphql'
import { RequiredChip } from '~/components/common/RequiredChip'
import { colorPalette } from '~/constants/styles/colors'
import { Campaign_SNS_LABEL } from '~/constants/snstype'
import { URL_VALIDATION_REGEXP } from '~/lib/constants'
import { useEffect, useRef, useContext } from 'react'
import { ValidationErrorMessage } from '~/components/common/ValidationErrorMessage'
import { INSTAGRAM_STORY_HASHTAG_TEXT } from '~/components/common/CampaignContents'
import { EXTERNAL_LINKS } from '~/lib/externalLinks'
import { CAMPAIGN_FORM_TITLE } from '~/constants/campaign'
import { LanguageSpecificTagsInput, TagListItem } from '~/features/campaign_input/LanguageSpecificTagsInput'
import { CreateCampaignContext } from '~/contexts/CreateCampaignContext'
import { SNS_TYPES, SNS_POST_TYPE_LIST_ARRAY, SNS_POST_TYPE_LIST_LABELS, SnsType } from '~/lib/sns'

export const snsSchemaFields = {
  snsRequirements: z.object({
    INSTAGRAM: z
      .object({
        checked: z.boolean(),
        requestUrl: z
          .string()
          .max(250, '文字数が上限に達しました。')
          .refine((val) => !URL_VALIDATION_REGEXP.test(val), {
            message: 'URLの形式は使用できません。',
          }),
        snsPostTypeList: z.record(z.boolean()),
        snsPostRedirectUrl: z.string().optional(),
        tagList: z.array(
          z.object({
            languageType: z.nativeEnum(LanguageType),
            tags: z.array(z.string()),
          }),
        ),
      })
      .superRefine((data, ctx) => {
        if (data.checked && !data.requestUrl) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '必須項目です',
            path: ['requestUrl'],
          })
          return z.NEVER
        }
        if (data.checked && data.snsPostTypeList[CampaignSnsRequirementSnsPostType.InstagramStories]) {
          if (!data.snsPostRedirectUrl) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'ストーリーズ投稿を選択している場合、必須項目です',
              path: ['snsPostRedirectUrl'],
            })
            return z.NEVER
          } else {
            if (data.snsPostRedirectUrl.length > 1024) {
              ctx.addIssue({
                code: z.ZodIssueCode.too_big,
                maximum: 1024,
                type: 'string',
                inclusive: true,
                message: 'URLは1024文字以内で入力してください。',
                path: ['snsPostRedirectUrl'],
              })
              return z.NEVER
            }
            if (!URL_VALIDATION_REGEXP.test(data.snsPostRedirectUrl)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'URLが正しくありません。URLの先頭が https:// または http://になっているか確認してください。',
                path: ['snsPostRedirectUrl'],
              })
              return z.NEVER
            }
          }
        }
        if (data.checked) {
          const allFalse = Object.values(data.snsPostTypeList).every((v) => v === false)
          if (allFalse) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'Instagramを選択している場合は、投稿形式を1つ以上選択する必要があります',
              path: ['snsPostTypeList'],
            })
            return z.NEVER
          }
        }
      }),
    TIKTOK: z.object({
      checked: z.boolean(),
      requestUrl: z.string().refine((val) => !URL_VALIDATION_REGEXP.test(val), {
        message: 'URLの形式は使用できません。',
      }),
      tagList: z.array(
        z.object({
          languageType: z.nativeEnum(LanguageType),
          tags: z.array(z.string()),
        }),
      ),
    }),
  }),
}

export const snsSchema = z.object(snsSchemaFields)
export function validateSns<T extends z.output<typeof snsSchema>>(v: T, ctx: z.RefinementCtx) {
  if (!Object.entries(v.snsRequirements).some(([_k, v]) => v.checked)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: '少なくとも1つのSNSを選択してください。',
      path: ['snsRequirements'],
    })
    return z.NEVER
  }
  return v
}

type Props = CampaignInputComponentProps

export const SNS = ({ description }: Props) => {
  const {
    formState: { errors },
  } = useFormContext<z.infer<typeof snsSchema>>()

  return (
    <CampaignInputComponent
      title="募集するSNS"
      required={true}
      description={description}
      compatibleFormatVersions={[FormatVersion.V4Campaign]}
    >
      <Grid container direction="column" gap={3}>
        {SNS_TYPES.map((snsType) => (
          <SnsItem key={snsType} snsType={snsType} />
        ))}
      </Grid>
      <FormControl error={!!errors.snsRequirements?.message}>
        <ValidationErrorMessage error={errors.snsRequirements?.message} />
      </FormControl>
    </CampaignInputComponent>
  )
}

const SnsItem = ({ snsType }: { snsType: SnsType }) => {
  const { campaign } = useContext(CreateCampaignContext)
  const { control } = useFormContext<z.infer<typeof snsSchema>>()
  const checkedSnsType = useWatch({ name: `snsRequirements.${snsType}.checked`, control })

  const desiredLanguages = campaign.desiredLanguages

  return (
    <Paper
      elevation={0}
      sx={{
        px: 4,
        pt: 2,
        pb: 3,
        border: `2px solid ${checkedSnsType ? colorPalette.info.outlineBorder : colorPalette.other.outlineBorder}`,
        borderRadius: 2,
      }}
    >
      <Grid container direction="column" gap={2}>
        <Controller
          name={`snsRequirements.${snsType}.checked`}
          control={control}
          render={({ field: { ref, ...field } }) => (
            <FormControlLabel
              sx={{ width: 'fit-content' }}
              control={<Checkbox {...field} checked={field.value} inputRef={ref} />}
              label={<Typography fontWeight="bold">{Campaign_SNS_LABEL[snsType]}</Typography>}
            />
          )}
        />

        <Grid container direction="column" gap={3} sx={{ pl: 4 }}>
          <AccountInput snsType={snsType} />
          {snsType === CampaignSnsRequirementSnsType.Instagram && <SnsPostTypeList />}
          {/* 中国語は対応していないので、中国語のみ選択されている場合はタグ入力を表示しない */}
          {desiredLanguages.length > 0 &&
            !(desiredLanguages.length === 1 && desiredLanguages.includes(LanguageType.Chinese)) && (
              <TagsInput snsType={snsType} />
            )}
        </Grid>
      </Grid>
    </Paper>
  )
}

const AccountInput = ({ snsType }: { snsType: SnsType }) => {
  const { control, clearErrors } = useFormContext<z.infer<typeof snsSchema>>()
  const checkedSnsType = useWatch({ name: `snsRequirements.${snsType}.checked`, control })
  const checkedInstagram = useWatch({ name: 'snsRequirements.INSTAGRAM.checked', control })

  useEffect(() => {
    if (!checkedInstagram) {
      clearErrors(`snsRequirements.INSTAGRAM.requestUrl`)
    }
  }, [checkedInstagram, clearErrors, snsType])

  return (
    <Grid container direction="column" gap={0.5}>
      <Stack direction="row" gap={0.5} alignItems="center">
        <Typography variant="body2" fontWeight="bold">
          インフルエンサーの投稿に載せるアカウント名
        </Typography>
        {snsType === CampaignSnsRequirementSnsType.Instagram && <RequiredChip />}
      </Stack>
      {snsType !== CampaignSnsRequirementSnsType.Instagram && (
        <Typography variant="caption" mb={1.5}>
          アカウントがある場合は、アカウント名を記載してください。
        </Typography>
      )}
      <Controller
        name={`snsRequirements.${snsType}.requestUrl`}
        control={control}
        render={({ field: { ref, ...field }, fieldState }) => (
          <TextField
            fullWidth
            variant="outlined"
            slotProps={{
              input: {
                startAdornment: <InputAdornment position="end">@</InputAdornment>,
              },
            }}
            size="small"
            placeholder="toridoribase"
            error={!!fieldState.error}
            helperText={checkedSnsType && <ValidationErrorMessage error={fieldState.error?.message} />}
            inputRef={ref}
            {...field}
            value={field.value || ''}
            onChange={(e) => {
              const value = e.target.value
              field.onChange(value)
            }}
            disabled={!checkedSnsType}
          />
        )}
      />
    </Grid>
  )
}

const SnsPostTypeList = () => {
  const {
    formState: { errors },
    control,
  } = useFormContext<z.infer<typeof snsSchema>>()
  const checkedSnsType = useWatch({ name: 'snsRequirements.INSTAGRAM.checked', control })
  const checkedStory = useWatch({
    name: `snsRequirements.INSTAGRAM.snsPostTypeList.${CampaignSnsRequirementSnsPostType.InstagramStories}`,
    control,
  })
  const inputRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (errors.snsRequirements?.INSTAGRAM?.snsPostTypeList) {
      inputRef.current?.scrollIntoView()
    }
  }, [errors])

  return (
    <Grid container direction="column" gap={0.5}>
      <Stack direction="row" gap={0.5} alignItems="center">
        <Typography variant="body2" fontWeight="bold">
          {CAMPAIGN_FORM_TITLE.snsPostTypeRestrictions}
        </Typography>
        <RequiredChip />
      </Stack>
      <Stack gap={0.5}>
        <Typography variant="caption">選択した投稿形式のいずれかで投稿されます。</Typography>
        <Typography variant="caption">
          インフルエンサーは選択した投稿形式のいずれかでのみ投稿可能です。
          <br />
          <Link href={EXTERNAL_LINKS.POST_FORMAT_RESTRICTIONS} target="_blank" rel="noopener noreferrer" sx={{ ml: 0 }}>
            詳しくはこちら
          </Link>
        </Typography>
      </Stack>
      <Stack direction={{ xs: 'column', sm: 'row' }}>
        <FormGroup row ref={inputRef}>
          {SNS_POST_TYPE_LIST_ARRAY.map((method, index) => (
            <Controller
              key={method}
              name={`snsRequirements.INSTAGRAM.snsPostTypeList.${method}`}
              control={control}
              render={({ field: { ref, ...field } }) => (
                <FormControlLabel
                  control={
                    <Checkbox
                      {...field}
                      checked={!!field.value}
                      disabled={!checkedSnsType}
                      inputRef={index === 0 ? ref : undefined}
                    />
                  }
                  label={SNS_POST_TYPE_LIST_LABELS[method]}
                />
              )}
            />
          ))}
        </FormGroup>
      </Stack>

      {errors.snsRequirements?.INSTAGRAM?.snsPostTypeList && checkedSnsType && (
        <ValidationErrorMessage
          error={errors.snsRequirements.INSTAGRAM.snsPostTypeList.message}
          sx={{ pt: 0, pl: 1 }}
        />
      )}

      {checkedStory && (
        <Grid container direction="column" gap={0.5}>
          <Alert severity="warning" icon={<WarningIcon />} sx={{ mb: 2 }}>
            <Typography variant="body1">ストーリーズ投稿に関する注意点</Typography>
            <List dense>
              <ListItem disableGutters>・投稿してから24時間後に自動的に削除されます</ListItem>
              <ListItem disableGutters>・インサイト情報は取得できません</ListItem>
              <ListItem disableGutters>・ハイライト保存後に投稿を削除された場合検知できません</ListItem>
              <ListItem disableGutters>・{INSTAGRAM_STORY_HASHTAG_TEXT}</ListItem>
            </List>
          </Alert>
          <Stack direction="row" gap={0.5} alignItems="center">
            <Typography variant="body2" fontWeight="bold">
              投稿の遷移先URL
            </Typography>
            <RequiredChip />
          </Stack>
          <Typography variant="caption">
            投稿形式でストーリーズを選択した場合、投稿の遷移先URLを記載してください。
          </Typography>
          <Typography variant="caption" pb={1.5}>
            遷移したいURLがない場合、「フィード」「リール」での募集がおすすめです。
          </Typography>
          <Controller
            name="snsRequirements.INSTAGRAM.snsPostRedirectUrl"
            control={control}
            render={({ field: { ref, ...field }, fieldState }) => (
              <TextField
                fullWidth
                placeholder="https://marketing.toridori.me"
                error={!!fieldState.error}
                helperText={checkedSnsType && <ValidationErrorMessage error={fieldState.error?.message} />}
                inputRef={ref}
                {...field}
                disabled={!checkedSnsType}
              />
            )}
          />
        </Grid>
      )}
    </Grid>
  )
}

const TagsInput = ({ snsType }: { snsType: SnsType }) => {
  const {
    setError,
    clearErrors,
    formState: { errors },
    control,
    setValue,
  } = useFormContext<z.infer<typeof snsSchema>>()

  const { campaign } = useContext(CreateCampaignContext)

  const checkedSnsType = useWatch({ name: `snsRequirements.${snsType}.checked`, control })
  const tagList = useWatch({ name: `snsRequirements.${snsType}.tagList`, control })
  const desiredLanguages = campaign.desiredLanguages

  const handleTagListChange = (updatedTagList: TagListItem[]) => {
    setValue(`snsRequirements.${snsType}.tagList`, updatedTagList, { shouldDirty: true })
  }

  const handleError = (errorMessage?: string) => {
    if (errorMessage) {
      setError(`snsRequirements.${snsType}.tagList`, { message: errorMessage })
    } else {
      clearErrors(`snsRequirements.${snsType}.tagList`)
    }
  }

  return (
    <LanguageSpecificTagsInput
      snsType={snsType}
      tagList={tagList}
      errorMessage={errors.snsRequirements?.[snsType]?.tagList?.message}
      disabled={!checkedSnsType}
      desiredLanguages={desiredLanguages}
      onChange={handleTagListChange}
      onError={handleError}
    />
  )
}
